<template>
  <div class="text-medium">
    <div class="field d-flex align-items-center">
      <span class="mr-2">{{ field.label }}:</span>
      <pill-dropdown
        toggle-class="pill-button"
        :options="operatorOptions"
        :selected="selectedOperator"
        @change="onOperatorSelection"
        class="mr-2"
      />
      <condition-selector-value-field
        :field="field"
        :operator="selectedOperator"
        @change="onValueChange"
        :org-id="orgId"
        :value="selectedValue"
        :timeline="timeline"
        :filter-configuration="filterConfiguration"
      />
    </div>
  </div>
</template>

<script>
import PillDropdown from '@/components/board/PillDropdown'
import ConditionSelectorValueField from '@/components/conditionSelector/ConditionSelectorValueField'
import { fieldTypeOperatorMapping, Operator } from '@/constants/initiative/filter'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import deserializeFilterOp from '@/utils/initiative/deserializeFilterOp'
import getOperatorCardinality from '@/utils/initiative/getOperatorCardinality'
import serializeFilterOp from '@/utils/initiative/serializeFilterOp'

/**
 * This component provides selection of a filter expression. filter expression may look like: due_date~lte~["2022-02-02"]
 */
export default {
  name: 'ConditionSelector',
  props: {
    field: Object,
    orgId: Number,
    /**
     * value should be in format <field><operator>[<comma_separated_value>]. e.g due_date~lte~["2022-02-02"]
     */
    value: {
      type: String,
      default: ''
    },
    /**
     * This is filter meta data which contains the mapping for field types and operations supported on each type. Sample metadata looks like
     * {
     *   fields: {
     *     due_date: 'date'
     *   },
     *   types: {
     *     date: ['is', 'lte', 'gte']
     *   }
     * }
     */
    filterConfiguration: Object,
    /**
     * This is selected timeline filter. It is getting used to fetch the goals of selected timeline only in KR filter
     */
    timeline: String
  },
  components: {
    PillDropdown,
    ConditionSelectorValueField
  },
  mixins: [ListNavigationMixin],
  computed: {
    operatorOptions() {
      const operatorTypes = this.filterConfiguration.types[this.field.type]
      const options = []
      operatorTypes.forEach(operator => {
        options.push({
          id: operator,
          ...fieldTypeOperatorMapping.default[operator],
          ...fieldTypeOperatorMapping[this.field.type]?.[operator]
        })
        const operatorInfo = this.filterConfiguration.operators[operator]
        if (operatorInfo.invertable) {
          const operatorId = `not-${operator}`
          options.push({
            id: operatorId,
            ...fieldTypeOperatorMapping.default[operatorId]
          })
        }
      })
      return options
    },
    deserializedValue() {
      return deserializeFilterOp(this.value)
    },
    selectedOperator() {
      return this.deserializedValue.operator || this.operatorOptions[0].id
    },
    selectedValue() {
      return this.deserializedValue.value
    },
    selectedField() {
      return this.deserializedValue.field
    }
  },
  methods: {
    hasCardinalityChanged(newOperator) {
      const oldCardinality = getOperatorCardinality(this.filterConfiguration, this.selectedOperator)
      const newCardinality = getOperatorCardinality(this.filterConfiguration, newOperator)
      return oldCardinality !== newCardinality
    },
    onOperatorSelection({ id: operator }) {
      const cardinalityChanged = this.hasCardinalityChanged(operator)
      let { field, value } = deserializeFilterOp(this.value)
      if ([Operator.SET, Operator.NOT_SET].includes(operator) || cardinalityChanged) {
        value = []
      }
      const expression = serializeFilterOp(field || this.field.id, operator, value)
      this.$emit('change', expression)
    },
    onValueChange(value) {
      const expression = serializeFilterOp(
        this.selectedField || this.field.id,
        this.selectedOperator,
        !Array.isArray(value) ? [value] : value
      )
      this.$emit('change', expression)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_input.scss';

.field {
  background-color: $quinary;
  border-radius: 20px;
  padding: 4px 10px 4px 10px;

  &:focus {
    outline: none;
  }
}

/deep/ .pill-button {
  background-color: $quaternary;

  &:hover,
  &:focus,
  &[aria-expanded='true'] {
    background-color: $tertiary;
  }
}
</style>
